import {
  Button,
  Input,
  Modal,
  TextArea,
  SingleDatePicker,
  Toggle,
  useNotification,
  Select,
} from '@prio365/prio365-react-library';
import { isValid, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getProject,
  getUserMe,
  RootReducerState,
} from '../../../../../../apps/main/rootReducer';
import Flex from '../../../../../../components/Flex';
import { PrioShareFileDto } from '../../../../../../models/PrioShare';
import { Project } from '../../../../../../models/Project';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import AddressSelectMail from '../../../../../mail/components/AddressSelectMail';
import { usePrioShareDraft } from '../../PrioShareDraftContext';
import { useDeletePrioShareFile } from '../../hooks/useDeletePrioShareFile';
import { useSavePrioShareDraft } from '../../hooks/useSavePrioShareDraft';
import PrioShareCollapsibleSection from '../PrioShareCollapsibleSection';
import PrioShareFileUploadZone from '../PrioShareFileUploadZone';
import DistributionListButton from '../DistributionListButton';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    gap: theme.spacing.regular,
    padding: `${theme.spacing.small}px 0`,
    overflow: 'auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.regular,
  },
  label: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
  toggleLabel: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.default,
  },
}));

interface PrioShareFormProps {
  onCancel: () => void;
  onCreated: () => void;
}

interface PrioShareFormState {
  name: string;
  messageText: string;
  uploadedFiles: PrioShareFileDto[];
  recipientEmails: string[];
  notificationEmails: string[];
  expirationDate: string;
  sendFromProject: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PrioShareForm: React.FC<PrioShareFormProps> = ({
  onCancel,
  onCreated,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const [notification, notificationContextHolder] = useNotification();

  const {
    draft,
    projectId,
    projectExtensionId,
    publishShare,
    isPublishing,
    isDraftReady,
  } = usePrioShareDraft();
  const { mutateAsync: saveDraft, isLoading: isSaving } =
    useSavePrioShareDraft();
  const me = useSelector(getUserMe);
  const { mutateAsync: deleteFile } = useDeletePrioShareFile();

  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [formState, setFormState] = useState<PrioShareFormState>({
    name: '',
    messageText: '',
    uploadedFiles: [],
    recipientEmails: [],
    notificationEmails: [],
    expirationDate: '',
    sendFromProject: true,
  });

  const sharedMailboxExtensionId = project?.sharedMailboxUserId;
  // No usable shared mailbox (e.g. subproject, or no access) → force personal
  // mailbox and lock the toggle. sharedMailboxUserId is the access-aware signal.
  const hasSharedMailbox = !!sharedMailboxExtensionId;
  const effectiveSendFromProject =
    hasSharedMailbox && formState.sendFromProject;

  const notificationEmailOptions = useMemo(() => {
    const options: {
      label: React.ReactNode;
      value: string;
    }[] = [];
    if (me?.mail) {
      options.push({
        label: t(
          'common:widgetArea.prioShareWidget.createForm.personalMailbox',
          { mail: me.mail }
        ),
        value: me.mail,
      });
    }
    if (
      sharedMailboxExtensionId &&
      project?.eMailPrefix &&
      project?.eMailSuffix
    ) {
      const projectMail = `${project.eMailPrefix}@${project.eMailSuffix}`;
      options.push({
        label: t(
          'common:widgetArea.prioShareWidget.createForm.projectMailbox',
          { mail: projectMail }
        ),
        value: projectMail,
      });
    }
    return options;
  }, [me, project, sharedMailboxExtensionId, t]);

  const [publishModalVisible, setPublishModalVisible] = useState(false);

  const isFormValid =
    formState.name.trim().length > 0 &&
    formState.recipientEmails.length > 0 &&
    formState.expirationDate.length > 0 &&
    formState.uploadedFiles.length > 0;
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleNameChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, name: value }));
  }, []);

  const handleMessageChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, messageText: value }));
  }, []);

  const handleFileAdded = useCallback((file: PrioShareFileDto) => {
    setFormState((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, file],
    }));
  }, []);

  const handleFileRemoved = useCallback(
    async (fileId: string) => {
      if (!draft) return;
      try {
        await deleteFile({
          projectExtensionId,
          prioShareId: draft.prioShareId,
          prioShareFileId: fileId,
        });
        setFormState((prev) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.filter(
            (f) => f.prioShareFileId !== fileId
          ),
        }));
      } catch {
        notification.error({
          title: t(
            'common:widgetArea.prioShareWidget.createForm.deleteFileError'
          ),
        });
      }
    },
    [draft, deleteFile, projectExtensionId, notification, t]
  );

  const handleRecipientsChange = useCallback((values: string[]) => {
    setFormState((prev) => ({
      ...prev,
      recipientEmails: values.filter((v) => EMAIL_REGEX.test(v)),
    }));
  }, []);

  const handleNotificationEmailsChange = useCallback((values: string[]) => {
    setFormState((prev) => ({
      ...prev,
      notificationEmails: values.filter((v) => EMAIL_REGEX.test(v)),
    }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!isDraftReady || !draft) return;
    if (!formState.name.trim()) {
      notification.error({
        title: t('common:widgetArea.prioShareWidget.createForm.nameRequired'),
      });
      return;
    }
    setPublishModalVisible(true);
  }, [isDraftReady, draft, formState.name, notification, t]);

  const handlePublishConfirm = useCallback(async () => {
    if (!draft) return;
    setPublishModalVisible(false);
    try {
      const result = await publishShare({
        prioShareId: draft.prioShareId,
        name: formState.name,
        expirationDate: formState.expirationDate,
        recipients: formState.recipientEmails.map((email) => ({
          email,
        })),
        notificationEmails: formState.notificationEmails,
        messageText: formState.messageText,
        senderType: effectiveSendFromProject ? 'projectMailbox' : 'user',
        senderMailboxId: effectiveSendFromProject
          ? sharedMailboxExtensionId
          : '',
        projectExtensionId,
      });

      if (result.result.ok) {
        notification.success({
          title: t('common:widgetArea.prioShareWidget.createForm.success'),
        });
        onCreated();
      } else {
        notification.error({
          title: t('common:widgetArea.prioShareWidget.createForm.error'),
        });
      }
    } catch {
      notification.error({
        title: t('common:widgetArea.prioShareWidget.createForm.error'),
      });
    }
  }, [
    formState,
    projectExtensionId,
    onCreated,
    notification,
    t,
    publishShare,
    draft,
    sharedMailboxExtensionId,
    effectiveSendFromProject,
  ]);

  const handleSave = useCallback(async () => {
    if (!isDraftReady || !draft) return;
    try {
      await saveDraft({
        projectExtensionId,
        prioShareId: draft.prioShareId,
        name: formState.name,
        expirationDate: formState.expirationDate,
        recipients: formState.recipientEmails.map((email) => ({ email })),
        notificationEmails: formState.notificationEmails,
        messageText: formState.messageText,
        senderType: effectiveSendFromProject ? 'projectMailbox' : 'user',
        senderMailboxId: effectiveSendFromProject
          ? sharedMailboxExtensionId
          : '',
      });
      notification.success({
        title: t('common:widgetArea.prioShareWidget.createForm.saveSuccess'),
      });
      onCancel();
    } catch {
      notification.error({
        title: t('common:widgetArea.prioShareWidget.createForm.saveError'),
      });
    }
  }, [
    draft,
    isDraftReady,
    formState,
    saveDraft,
    onCancel,
    notification,
    t,
    projectExtensionId,
    sharedMailboxExtensionId,
    effectiveSendFromProject,
  ]);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (!draft) return;
    setFormState((prev) => ({
      ...prev,
      name: draft.name ?? '',
      messageText: draft.messageText ?? '',
      recipientEmails: draft.recipients?.map((r) => r.email) ?? [],
      notificationEmails: draft.notificationEmails ?? [],
      expirationDate: draft.expirationDate ?? '',
      uploadedFiles: draft.files ?? [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.prioShareId]);
  //#endregion

  return (
    <div className={classes.root}>
      {notificationContextHolder}
      <div className={classes.form}>
        {/* Titel field */}
        <Flex.Column>
          <span className={classes.label}>
            {t('common:widgetArea.prioShareWidget.createForm.nameLabel')}
          </span>
          <Input
            value={formState.name}
            onChange={handleNameChange}
            placeholder={t(
              'common:widgetArea.prioShareWidget.createForm.namePlaceholder'
            )}
            full
          />
        </Flex.Column>

        {/* Anschreiben field */}
        <Flex.Column>
          <span className={classes.label}>
            {t('common:widgetArea.prioShareWidget.createForm.messageLabel')}
          </span>
          <TextArea
            value={formState.messageText}
            onChange={handleMessageChange}
            placeholder={t(
              'common:widgetArea.prioShareWidget.createForm.messagePlaceholder'
            )}
            charLimit={2000}
          />
        </Flex.Column>

        {/* Dateien section */}
        <PrioShareCollapsibleSection
          title={t('common:widgetArea.prioShareWidget.createForm.filesLabel')}
          count={formState.uploadedFiles.length}
        >
          <PrioShareFileUploadZone
            uploadedFiles={formState.uploadedFiles}
            onFileAdded={handleFileAdded}
            onFileRemoved={handleFileRemoved}
          />
        </PrioShareCollapsibleSection>

        {/* Empfaenger section */}
        <PrioShareCollapsibleSection
          title={t(
            'common:widgetArea.prioShareWidget.createForm.recipientsLabel'
          )}
          count={formState.recipientEmails.length}
        >
          <span className={classes.label}>
            {t('common:widgetArea.prioShareWidget.createForm.addRecipient')}
          </span>
          <Flex.Row alignItems="center" childrenGap={8}>
            <div style={{ flex: 1 }}>
              <AddressSelectMail
                value={formState.recipientEmails}
                changeHandler={handleRecipientsChange}
                projectId={projectId}
                placeholder={t(
                  'common:widgetArea.prioShareWidget.createForm.addPlaceholder'
                )}
                bordered
                closableTags
                maxCount="responsive"
                height={40}
              />{' '}
            </div>
            <DistributionListButton
              value={formState.recipientEmails}
              onChange={handleRecipientsChange}
              projectId={projectId}
            />
          </Flex.Row>
        </PrioShareCollapsibleSection>

        {/* Einstellungen section */}
        <PrioShareCollapsibleSection
          title={t(
            'common:widgetArea.prioShareWidget.createForm.settingsLabel'
          )}
        >
          <Flex.Column childrenGap={12}>
            {/* Notification Emails */}
            <Flex.Column childrenGap={4}>
              <span className={classes.label}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.notificationEmailsLabel'
                )}
              </span>
              <Select
                mode="tags"
                showSearch={false}
                value={formState.notificationEmails}
                onChange={handleNotificationEmailsChange}
                placeholder={t(
                  'common:widgetArea.prioShareWidget.createForm.addPlaceholder'
                )}
                style={{ width: '100%' }}
                options={notificationEmailOptions}
              />
            </Flex.Column>

            {/* Expiration Date */}
            <Flex.Column childrenGap={4}>
              <span className={classes.label}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.expirationDateLabel'
                )}
              </span>
              <SingleDatePicker
                value={
                  formState.expirationDate &&
                  isValid(parseISO(formState.expirationDate)) &&
                  parseISO(formState.expirationDate).getFullYear() > 1900
                    ? {
                        dateValue: parseISO(formState.expirationDate),
                        utsn: null,
                      }
                    : undefined
                }
                onChange={(d) => {
                  if (d?.dateValue) {
                    setFormState((prev) => ({
                      ...prev,
                      expirationDate: d.dateValue.toISOString(),
                    }));
                  }
                }}
              />
            </Flex.Column>

            {/* Send from Project */}
            <Flex.Row alignItems="center" justifyContent="space-between">
              <span className={classes.toggleLabel}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.sendFromProjectLabel'
                )}
              </span>
              <Toggle
                checked={effectiveSendFromProject}
                disabled={!hasSharedMailbox}
                onChange={(checked) =>
                  setFormState((prev) => ({
                    ...prev,
                    sendFromProject: checked,
                  }))
                }
              />
            </Flex.Row>
          </Flex.Column>
        </PrioShareCollapsibleSection>
      </div>

      {/* Footer */}
      <Flex.Row
        justifyContent="space-between"
        alignItems="center"
        style={{ width: '100%' }}
      >
        <Button type="default" onClick={onCancel}>
          {t('common:widgetArea.prioShareWidget.createForm.cancel')}
        </Button>
        <Flex.Row childrenGap={8}>
          <Button
            type="default"
            onClick={handleSave}
            disabled={isSaving || !isDraftReady || !isFormValid}
            loading={isSaving}
          >
            {t('common:save')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={isPublishing || !isDraftReady || !isFormValid}
            loading={isPublishing || !isDraftReady}
          >
            {t('common:widgetArea.prioShareWidget.createForm.submit')}
          </Button>
        </Flex.Row>
      </Flex.Row>

      {/* Publish Confirmation Modal */}
      <Modal
        title={t('common:widgetArea.prioShareWidget.publishConfirmTitle')}
        visible={publishModalVisible}
        onOk={handlePublishConfirm}
        onClose={() => setPublishModalVisible(false)}
        okText={t('common:widgetArea.prioShareWidget.publishConfirmOk')}
        cancelText={t('common:cancel')}
      >
        {t('common:widgetArea.prioShareWidget.publishConfirmContent')}
      </Modal>
    </div>
  );
};

export default PrioShareForm;
